// src/frontend/pages/ContentViewerBlockchain.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BITCOIN_MASTER_COURSE_CAPS } from "../data/books/BitcoinMasterCourse/index";
import {  BOOK_BITCOIN_MASTER_COURSE_CONTENTS } from "../data/books/BitcoinMasterCourse/contents";

/* ====== Estilo/UX ====== */
const TITLE_BLUE = "#175cd3";
const SIDEBAR_BG = "#0f2147";
const SIDEBAR_BORDER = "#0b1a3a";
const BASE_PATH = "/course/bitcoin-master";

/** Índice lateral (sidebar) desde el libro */
const contentsData = BOOK_BITCOIN_MASTER_COURSE_CONTENTS;

/** Registro de “construcciones” usando BLOCKCHAIN_FUNDAMENTALS_CAPS */
const ComponentRegistry = Object.fromEntries(
  Object.entries(BITCOIN_MASTER_COURSE_CAPS || {}).map(([key, data]) => [
    key,
    (props) => <ChapterRenderer data={data} {...props} />,
  ])
);

/* ---------- Utils ---------- */
function slugify(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function ensureAnchorId(section, fallbackPrefix = "sec") {
  if (section.anchor) return section.anchor;
  if (section.id) return section.id;
  if (section.heading) return slugify(section.heading);
  return `${fallbackPrefix}-${Math.random().toString(36).slice(2, 8)}`;
}

// helper para números 01, 02, 03…
const pad2 = (n) => String(n).padStart(2, "0");

/* ---------- Resolver rutas de imagen --------- */
function resolveAssetSrc(raw) {
  if (!raw) return "";
  const isHttp = /^https?:\/\//i.test(raw);
  if (isHttp) return raw;

  const viteBase =
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.BASE_URL) ||
    "";
  const craBase =
    typeof process !== "undefined" ? process.env.PUBLIC_URL || "" : "";
  const basePrefix = viteBase || craBase || "";

  if (raw.startsWith("/")) {
    return `${basePrefix.replace(/\/$/, "")}${raw}`;
  }

  try {
    const imported = new URL(`../assets/${raw}`, import.meta.url).href;
    return imported;
  } catch {
    return raw;
  }
}

/** ====== Glosario / Markdown-lite ====== */
function useGlossaryIndex() {
  const [index, setIndex] = React.useState({ regex: null, dict: {}, size: 0 });

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = await import(
          "../data/books/FundamentosBlockchain/data/cap15"
        );
        const rootGloss = Array.isArray(mod?.default?.glossary)
          ? mod.default.glossary
          : [];
        const secGloss = Array.isArray(mod?.default?.sections)
          ? mod.default.sections.find((s) => Array.isArray(s.glossary))
              ?.glossary ?? []
          : [];
        const glossary = rootGloss.length ? rootGloss : secGloss;

        if (!glossary?.length) {
          if (mounted) setIndex({ regex: null, dict: {}, size: 0 });
          return;
        }

        const dict = {};
        const add = (term, def) => {
          const t = String(term || "").trim();
          if (!t) return;
          dict[t.toLowerCase()] = def;
        };

        for (const item of glossary) {
          const term = String(item?.termino ?? "").trim();
          const def = String(item?.definicion ?? "").trim();
          if (!term || !def) continue;

          add(term, def);

          const m = term.match(/^(.*?)\s*\((.*?)\)\s*$/);
          if (m) {
            const left = m[1].trim();
            const inside = m[2].trim();
            if (left) add(left, def);
            if (inside) add(inside, def);
          }
        }

        const keys = Object.keys(dict);
        if (!keys.length) {
          if (mounted) setIndex({ regex: null, dict: {}, size: 0 });
          return;
        }

        const escaped = keys
          .sort((a, b) => b.length - a.length)
          .map(escapeRegex);
        const group = `(?:${escaped.join("|")})`;
        const boundary = `(?<![\\p{L}\\p{N}_])${group}(?![\\p{L}\\p{N}_])`;
        const regex = new RegExp(boundary, "giu");

        if (mounted) setIndex({ regex, dict, size: keys.length });
      } catch (e) {
        console.warn(
          "[Glosario] No se pudo cargar cap15.js en FundamentosBlockchain:",
          e
        );
        if (mounted) setIndex({ regex: null, dict: {}, size: 0 });
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return index;
}

function escapeRegex(s = "") {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function splitByCode(str = "") {
  const parts = [];
  let rest = str;
  let idx = 0;
  while (true) {
    const start = rest.indexOf("`");
    if (start < 0) {
      parts.push({ type: "text", text: rest });
      break;
    }
    const end = rest.indexOf("`", start + 1);
    if (end < 0) {
      parts.push({ type: "text", text: rest });
      break;
    }
    const before = rest.slice(0, start);
    const code = rest.slice(start + 1, end);
    if (before) parts.push({ type: "text", text: before });
    parts.push({ type: "code", text: code });
    rest = rest.slice(end + 1);
    idx++;
    if (idx > 2000) break;
  }
  return parts;
}

function splitByBold(str = "") {
  const out = [];
  let rest = str;
  let guard = 0;
  while (true) {
    const s = rest.indexOf("**");
    if (s < 0) {
      if (rest) out.push({ bold: false, text: rest });
      break;
    }
    const e = rest.indexOf("**", s + 2);
    if (e < 0) {
      out.push({ bold: false, text: rest });
      break;
    }
    const before = rest.slice(0, s);
    const bold = rest.slice(s + 2, e);
    if (before) out.push({ bold: false, text: before });
    out.push({ bold: true, text: bold });
    rest = rest.slice(e + 2);
    guard++;
    if (guard > 2000) break;
  }
  return out;
}

/** Resalta términos del glosario */
function highlightGlossary(str = "", glossaryIndex) {
  const { regex, dict } = glossaryIndex || {};
  if (!regex) return str;

  const nodes = [];
  let lastIndex = 0;
  const text = String(str);

  regex.lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const start = m.index;
    const end = regex.lastIndex;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const term = m[0];
    const def = dict[term.toLowerCase()];
    nodes.push(
      <abbr key={`${start}-${end}`} className="vv-gloss" title={def || term}>
        {term}
      </abbr>
    );
    lastIndex = end;
    if (nodes.length > 4000) break;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/** Links + glosario */
function renderWithLinksAndGloss(text = "", glossaryIndex, onOpenFrame) {
  const parts = [];
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    const { index } = match;
    const label = match[1];
    const url = match[2];

    if (index > lastIndex) {
      parts.push({
        type: "text",
        text: text.slice(lastIndex, index),
      });
    }

    parts.push({
      type: "link",
      label,
      url,
    });

    lastIndex = linkRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      text: text.slice(lastIndex),
    });
  }

  return parts.map((part, idx) => {
    if (part.type === "link") {
      const handleClick = () => {
        if (onOpenFrame) {
          onOpenFrame(part.url, part.label);
        }
      };

      return (
        <button
          key={`link-${idx}`}
          type="button"
          className="vv-linkFrame"
          onClick={handleClick}
        >
          <span>{part.label}</span>
          <span className="vv-linkFrame-icon">⧉</span>
        </button>
      );
    }

    const nodes = highlightGlossary(part.text, glossaryIndex);
    return (
      <React.Fragment key={`txt-${idx}`}>{nodes}</React.Fragment>
    );
  });
}

function RichText({ text = "", glossaryIndex, onOpenFrame }) {
  const codeChunks = splitByCode(text);
  return (
    <>
      {codeChunks.map((chunk, i) => {
        if (chunk.type === "code") {
          return (
            <code key={`c${i}`} className="vv-inline-code">
              {chunk.text}
            </code>
          );
        }
        const boldChunks = splitByBold(chunk.text);
        return boldChunks.map((b, j) => {
          const nodes = renderWithLinksAndGloss(
            b.text,
            glossaryIndex,
            onOpenFrame
          );
          if (b.bold) {
            return <strong key={`b${i}-${j}`}>{nodes}</strong>;
          }
          return (
            <React.Fragment key={`t${i}-${j}`}>{nodes}</React.Fragment>
          );
        });
      })}
    </>
  );
}

/* === Bloque de NOTAS (note/notes/footnote/footnotes) === */
function NotesBlock({ notes, title = "Notas", glossaryIndex, onOpenFrame }) {
  if (!notes) return null;
  const list = Array.isArray(notes) ? notes : [notes];
  if (!list.length) return null;

  return (
    <aside className="vv-notes">
      <div className="vv-notes-title">{title}</div>
      <ul>
        {list.map((n, i) => (
          <li key={i}>
            <RichText
              text={String(n)}
              glossaryIndex={glossaryIndex}
              onOpenFrame={onOpenFrame}
            />
          </li>
        ))}
      </ul>
    </aside>
  );
}

/* ==== Bloques BLUEPOINT (para resúmenes / ideas clave) ==== */
function Bluepoints({ items, glossaryIndex, onOpenFrame }) {
  if (!items) return null;
  const list = Array.isArray(items) ? items : [items];
  if (!list.length) return null;

  return (
    <div className="vv-bluepoints">
      {list.map((bp, idx) => {
        if (typeof bp === "string") {
          return (
            <article key={idx} className="vv-bluepoint">
              <div className="vv-bluepoint-badge" />
              <div className="vv-bluepoint-body">
                <RichText
                  text={bp}
                  glossaryIndex={glossaryIndex}
                  onOpenFrame={onOpenFrame}
                />
              </div>
            </article>
          );
        }

        const heading = bp.heading || bp.title || bp.label;
        const body =
          typeof bp.text === "string"
            ? bp.text
            : Array.isArray(bp.text)
            ? bp.text.join("\n")
            : bp.body || "";

        return (
          <article key={idx} className="vv-bluepoint">
            <div className="vv-bluepoint-badge" />
            <div className="vv-bluepoint-body">
              {heading && (
                <h4 className="vv-bluepoint-title">{heading}</h4>
              )}
              {body && (
                <p>
                  <RichText
                    text={body}
                    glossaryIndex={glossaryIndex}
                    onOpenFrame={onOpenFrame}
                  />
                </p>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

/* ===== Helpers para código: detección de lenguaje ===== */

function getLangLabel(lang) {
  const l = (lang || "").toLowerCase();
  switch (l) {
    case "javascript":
    case "js":
      return "JavaScript";
    case "solidity":
      return "Solidity";
    case "java":
      return "Java";
    case "python":
      return "Python";
    case "rust":
      return "Rust";
    case "haskell":
      return "Haskell";
    case "p5js":
    case "p5":
      return "p5.js";
    default:
      return lang ? lang.toUpperCase() : "";
  }
}

function getCodeTextAndLang(block, which = "code") {
  if (!block) return { text: "", lang: "" };
  const lang =
    block.codeLang ||
    block.lang ||
    block.language ||
    block[`${which}Lang`] ||
    "";
  const raw = block[which];
  let text = "";

  if (typeof raw === "string") {
    text = raw;
  } else if (raw && typeof raw === "object") {
    text = String(raw.value ?? raw.text ?? raw.code ?? "");
  }

  return { text, lang: (lang || "").toLowerCase() };
}

/* ====== Renderer capítulo ====== */
function ChapterRenderer({
  data,
  onOpenFrame,
  courseName = "Blockchain Esencial",
  onGoToCourseRoot,
}) {
  const glossaryIndex = useGlossaryIndex();

  const safeData = data ?? { title: "", sections: [] };
  const {
    title,
    sections = [],
    notes: dataNotes,
    note: dataNote,
    footnotes: dataFootnotes,
    footnote: dataFootnote,
  } = safeData;

  const processedSections = useMemo(() => {
    const slugifyLocal = (str = "") =>
      String(str)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

    const ensureAnchorIdLocal = (section, fallbackPrefix = "sec") => {
      if (section.anchor) return section.anchor;
      if (section.id) return section.id;
      if (section.heading) return slugifyLocal(section.heading);
      return `${fallbackPrefix}-${Math.random().toString(36).slice(2, 8)}`;
    };

    return (sections || []).map((s, idx) => {
      const anchor = ensureAnchorIdLocal(s, `sec-${idx + 1}`);
      const subsections = Array.isArray(s.subsections)
        ? s.subsections.map((sub, j) => ({
            ...sub,
            _anchor: ensureAnchorIdLocal(sub, `sub-${idx + 1}-${j + 1}`),
          }))
        : [];
      return { ...s, _anchor: anchor, subsections };
    });
  }, [sections]);

  const tocItems = useMemo(
    () =>
      processedSections.map((s) => ({
        anchor: s._anchor,
        label: s.heading || "Sección",
        subs: (s.subsections || []).map((sub) => ({
          anchor: sub._anchor,
          label: sub.heading || "Subsección",
        })),
      })),
    [processedSections]
  );

  const jump = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.scrollBy({
        top: -80,
        left: 0,
        behavior: "instant",
      });
    }
  };

  const hasContent = Boolean(title) || (sections && sections.length > 0);

  return (
    <article className="vv-article">
      {!hasContent ? (
        <div className="vv-card">
          <p style={{ margin: 0, color: "#111827" }}>
            No hay datos para este capítulo.
          </p>
        </div>
      ) : (
        <>
          {title && <h2>{title}</h2>}

          {/* BREADCRUMB BAJO EL TÍTULO */}
          <nav className="vv-breadcrumb" aria-label="Ruta de navegación">
            {courseName && (
              <>
                <button
                  type="button"
                  className="vv-breadcrumb-link"
                  onClick={onGoToCourseRoot}
                >
                  {courseName}
                </button>
                <span className="vv-breadcrumb-sep">›</span>
              </>
            )}
            <span className="vv-breadcrumb-current">
              {title || "Lección actual"}
            </span>
          </nav>

          {tocItems.length > 0 && (
            <nav
              className="vv-card vv-toc"
              aria-label="Índice del capítulo"
            >
              <h3 style={{ marginTop: 0 }}>Índice</h3>
              <ol className="vv-toc-list">
                {tocItems.map((t) => (
                  <li key={t.anchor}>
                    <button
                      className="vv-toc-link"
                      onClick={() => jump(t.anchor)}
                    >
                      {t.label}
                    </button>
                    {t.subs?.length > 0 && (
                      <ol className="vv-toc-sublist">
                        {t.subs.map((sb) => (
                          <li key={sb.anchor}>
                            <button
                              className="vv-toc-link"
                              onClick={() => jump(sb.anchor)}
                            >
                              {sb.label}
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {processedSections.map((s) => {
            const sectionBluepoints = s.bluepoints || s.bluepoint;

            const { text: sectionCodeText, lang: sectionCodeLang } =
              getCodeTextAndLang(s, "code");
            const {
              text: sectionPreText,
              lang: sectionPreLang,
            } = getCodeTextAndLang(s, "pre");

            return (
              <section
                key={s._anchor}
                className="vv-card"
                id={s._anchor}
              >
                {s.heading && <h3>{s.heading}</h3>}

                {(() => {
                  const img =
                    s?.image?.src
                      ? {
                          src: s.image.src,
                          alt: s.image.alt,
                          caption: s.image.caption,
                        }
                      : s?.type === "image" && s?.src
                      ? {
                          src: s.src,
                          alt: s.alt,
                          caption: s.caption,
                        }
                      : null;

                  if (!img) return null;
                  const resolved = resolveAssetSrc(img.src);

                  return (
                    <figure className="vv-figure">
                      <img
                        src={resolved}
                        alt={img.alt || ""}
                        onError={() =>
                          console.warn(
                            "[Imagen] No se pudo cargar:",
                            resolved
                          )
                        }
                      />
                      {img.caption && (
                        <figcaption>{img.caption}</figcaption>
                      )}
                    </figure>
                  );
                })()}

                {typeof s.text === "string"
                  ? s.text.split("\n").map((p, i) =>
                      p.trim() ? (
                        <p key={i}>
                          <RichText
                            text={p}
                            glossaryIndex={glossaryIndex}
                            onOpenFrame={onOpenFrame}
                          />
                        </p>
                      ) : (
                        <br key={`br-${i}`} />
                      )
                    )
                  : Array.isArray(s.text)
                  ? s.text.map((p, i) => (
                      <p key={i}>
                        <RichText
                          text={p}
                          glossaryIndex={glossaryIndex}
                          onOpenFrame={onOpenFrame}
                        />
                      </p>
                    ))
                  : null}

                {/* BLUEPOINTS a nivel sección */}
                <Bluepoints
                  items={sectionBluepoints}
                  glossaryIndex={glossaryIndex}
                  onOpenFrame={onOpenFrame}
                />

                {Array.isArray(s.list) && s.list.length > 0 && (
                  <ul className="vv-list">
                    {s.list.map((li, idx) => (
                      <li key={idx}>
                        <RichText
                          text={String(li)}
                          glossaryIndex={glossaryIndex}
                          onOpenFrame={onOpenFrame}
                        />
                      </li>
                    ))}
                  </ul>
                )}

                {s.table?.columns?.length > 0 &&
                  Array.isArray(s.table.rows) && (
                    <div className="vv-table-wrap">
                      <table className="vv-table" role="table">
                        <thead>
                          <tr>
                            {s.table.columns.map((c) => (
                              <th key={c} scope="col">
                                {c}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.table.rows.map((row, idx) => {
                            if (Array.isArray(row)) {
                              return (
                                <tr key={idx}>
                                  {row.map((cell, j) => (
                                    <td key={j}>
                                      <RichText
                                        text={String(cell)}
                                        glossaryIndex={glossaryIndex}
                                        onOpenFrame={onOpenFrame}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              );
                            }
                            return (
                              <tr key={idx}>
                                {s.table.columns.map((c) => (
                                  <td key={c}>
                                    <RichText
                                      text={String(row[c] ?? "")}
                                      glossaryIndex={glossaryIndex}
                                      onOpenFrame={onOpenFrame}
                                    />
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                {sectionCodeText && (
                  <pre
                    className="vv-code small"
                    data-lang={sectionCodeLang}
                    data-lang-label={getLangLabel(sectionCodeLang)}
                  >
                    <code>{sectionCodeText}</code>
                  </pre>
                )}
                {sectionPreText && (
                  <pre
                    className="vv-code small"
                    data-lang={sectionPreLang}
                    data-lang-label={getLangLabel(sectionPreLang)}
                  >
                    <code>{sectionPreText}</code>
                  </pre>
                )}

                {/* Notas a nivel sección */}
                <NotesBlock
                  notes={
                    s.notes || s.note || s.footnotes || s.footnote
                  }
                  glossaryIndex={glossaryIndex}
                  onOpenFrame={onOpenFrame}
                />

                {/* Subsections */}
                {Array.isArray(s.subsections) &&
                  s.subsections.map((sub) => {
                    const subBluepoints =
                      sub.bluepoints || sub.bluepoint;

                    const {
                      text: subCodeText,
                      lang: subCodeLang,
                    } = getCodeTextAndLang(sub, "code");
                    const {
                      text: subPreText,
                      lang: subPreLang,
                    } = getCodeTextAndLang(sub, "pre");

                    return (
                      <div
                        key={sub._anchor}
                        id={sub._anchor}
                        className="vv-subsection"
                      >
                        {sub.heading && <h4>{sub.heading}</h4>}

                        {(() => {
                          const img =
                            sub?.image?.src
                              ? {
                                  src: sub.image.src,
                                  alt: sub.image.alt,
                                  caption: sub.image.caption,
                                }
                              : sub?.type === "image" && sub?.src
                              ? {
                                  src: sub.src,
                                  alt: sub.alt,
                                  caption: sub.caption,
                                }
                              : null;

                          if (!img) return null;
                          const resolved = resolveAssetSrc(img.src);

                          return (
                            <figure className="vv-figure">
                              <img
                                src={resolved}
                                alt={img.alt || ""}
                                onError={() =>
                                  console.warn(
                                    "[Imagen] No se pudo cargar:",
                                    resolved
                                  )
                                }
                              />
                              {img.caption && (
                                <figcaption>{img.caption}</figcaption>
                              )}
                            </figure>
                          );
                        })()}

                        {typeof sub.text === "string"
                          ? sub.text.split("\n").map((p, i) =>
                              p.trim() ? (
                                <p key={i}>
                                  <RichText
                                    text={p}
                                    glossaryIndex={glossaryIndex}
                                    onOpenFrame={onOpenFrame}
                                  />
                                </p>
                              ) : (
                                <br key={`br-s-${i}`} />
                              )
                            )
                          : Array.isArray(sub.text)
                          ? sub.text.map((p, i) => (
                              <p key={i}>
                                <RichText
                                  text={p}
                                  glossaryIndex={glossaryIndex}
                                  onOpenFrame={onOpenFrame}
                                />
                              </p>
                            ))
                          : null}

                        {/* BLUEPOINTS a nivel subsección */}
                        <Bluepoints
                          items={subBluepoints}
                          glossaryIndex={glossaryIndex}
                          onOpenFrame={onOpenFrame}
                        />

                        {Array.isArray(sub.list) &&
                          sub.list.length > 0 && (
                            <ul className="vv-list">
                              {sub.list.map((li, idx) => (
                                <li key={idx}>
                                  <RichText
                                    text={String(li)}
                                    glossaryIndex={glossaryIndex}
                                    onOpenFrame={onOpenFrame}
                                  />
                                </li>
                              ))}
                            </ul>
                          )}

                        {sub.table?.columns?.length > 0 &&
                          Array.isArray(sub.table.rows) && (
                            <div className="vv-table-wrap">
                              <table
                                className="vv-table"
                                role="table"
                              >
                                <thead>
                                  <tr>
                                    {sub.table.columns.map((c) => (
                                      <th key={c} scope="col">
                                        {c}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {sub.table.rows.map((row, idx) => {
                                    if (Array.isArray(row)) {
                                      return (
                                        <tr key={idx}>
                                          {row.map((cell, j) => (
                                            <td key={j}>
                                              <RichText
                                                text={String(cell)}
                                                glossaryIndex={
                                                  glossaryIndex
                                                }
                                                onOpenFrame={
                                                  onOpenFrame
                                                }
                                              />
                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    }
                                    return (
                                      <tr key={idx}>
                                        {sub.table.columns.map((c) => (
                                          <td key={c}>
                                            <RichText
                                              text={String(
                                                row[c] ?? ""
                                              )}
                                              glossaryIndex={
                                                glossaryIndex
                                              }
                                              onOpenFrame={
                                                onOpenFrame
                                              }
                                            />
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}

                        {subCodeText && (
                          <pre
                            className="vv-code small"
                            data-lang={subCodeLang}
                            data-lang-label={getLangLabel(
                              subCodeLang
                            )}
                          >
                            <code>{subCodeText}</code>
                          </pre>
                        )}
                        {subPreText && (
                          <pre
                            className="vv-code small"
                            data-lang={subPreLang}
                            data-lang-label={getLangLabel(
                              subPreLang
                            )}
                          >
                            <code>{subPreText}</code>
                          </pre>
                        )}

                        {/* Notas a nivel subsección */}
                        <NotesBlock
                          notes={
                            sub.notes ||
                            sub.note ||
                            sub.footnotes ||
                            sub.footnote
                          }
                          glossaryIndex={glossaryIndex}
                          onOpenFrame={onOpenFrame}
                        />
                      </div>
                    );
                  })}
              </section>
            );
          })}

          {/* Notas globales del capítulo */}
          {(dataNotes || dataNote || dataFootnotes || dataFootnote) && (
            <section className="vv-card" id="notas-capitulo">
              <NotesBlock
                notes={
                  dataNotes ||
                  dataNote ||
                  dataFootnotes ||
                  dataFootnote
                }
                title="Notas del capítulo"
                glossaryIndex={glossaryIndex}
                onOpenFrame={onOpenFrame}
              />
            </section>
          )}
        </>
      )}
    </article>
  );
}

export default function ContentViewerBlockchain() {
  const { contentId } = useParams();
  const navigate = useNavigate();

  const [selected, setSelected] = useState(
    () =>
      contentsData.find((c) => c.id === Number(contentId)) ||
      contentsData[0]
  );

  const [collapsed, setCollapsed] = useState(false);

  const [frame, setFrame] = useState({
    open: false,
    url: "",
    label: "",
  });

  // Reaccionar al cambio de ID en la URL
  useEffect(() => {
    const found = contentsData.find(
      (c) => c.id === Number(contentId)
    );
    if (found) setSelected(found);
  }, [contentId]);

  // Reset de frame y scroll al cambiar de lección
  useEffect(() => {
    if (!selected) return;

    setFrame((prev) => ({
      ...prev,
      open: false,
      url: "",
      label: "",
    }));

    setTimeout(() => {
      const frameWrap = document.querySelector(".vv-frameWrap");
      const headerOffset = window.innerHeight * 0.11;

      if (frameWrap) {
        const rect = frameWrap.getBoundingClientRect();
        window.scrollTo({
          top: window.scrollY + rect.top - headerOffset,
          left: 0,
          behavior: "auto",
        });
      } else {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      }
    }, 0);
  }, [selected?.id]);

  const handleSelect = (item) =>
    navigate(`${BASE_PATH}/${item.id}`);

  const handleOpenFrame = (url, label) => {
    setFrame({
      open: true,
      url,
      label: label || url,
    });
  };

  const handleCloseFrame = () => {
    setFrame((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // ==== Cálculo de progreso y navegación siguiente/anterior ====
  const totalLessons = contentsData.length;
  const currentIndex = contentsData.findIndex(
    (c) => c.id === selected?.id
  );
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;

  const progressRatio =
    totalLessons > 0 ? (safeIndex + 1) / totalLessons : 0;
  const progressPercent = Math.round(progressRatio * 100);

  const prevItem =
    safeIndex > 0 ? contentsData[safeIndex - 1] : null;
  const nextItem =
    safeIndex < totalLessons - 1
      ? contentsData[safeIndex + 1]
      : null;

  const goPrev = () => {
    if (prevItem) navigate(`${BASE_PATH}/${prevItem.id}`);
  };
  const goNext = () => {
    if (nextItem) navigate(`${BASE_PATH}/${nextItem.id}`);
  };
  const goRoot = () => {
    if (contentsData[0]) {
      navigate(`${BASE_PATH}/${contentsData[0].id}`);
    }
  };

  const ActiveComponent =
    selected?.type === "external" &&
    selected?.component &&
    ComponentRegistry[selected.component];

  return (
    <div
      className="vv-serif-root vv-wrap"
      data-collapsed={collapsed ? "true" : "false"}
    >
      <style>{`
  :root { --header-offset: 11vh; }

  .vv-wrap{
    --sb-w-expanded: 340px;
    --sb-w-collapsed: 84px;
    --sb-w: var(--sb-w-expanded);
    display:block;
    min-height:100dvh;
    background:#ffffff;
  }
  .vv-wrap[data-collapsed="true"]{ --sb-w: var(--sb-w-collapsed); }

  .vv-sidebar{
    position:fixed;
    inset: var(--header-offset) auto 0 0;
    width:var(--sb-w);
    background:${SIDEBAR_BG};
    border-right:1px solid ${SIDEBAR_BORDER};
    display:grid;
    grid-template-rows:auto 1fr auto;
    z-index:30;
    overflow:hidden;
  }

  /* SOLO scroll vertical, sin barras visibles */
  .vv-side-scroll{
    overflow-y:auto;
    overflow-x:hidden;
    padding:10px 10px 16px 10px;
  }
  .vv-side-scroll::-webkit-scrollbar{
    width:0;
    height:0;
  }
  .vv-side-scroll{
    scrollbar-width:none; /* Firefox */
  }

  .vv-title{
    display:flex;
    align-items:center;
    gap:10px;
    background:${TITLE_BLUE};
    color:#fff;
    font-weight:800;
    font-size:16px;
    padding:16px 14px;
    margin:0;
    border-bottom:1px solid rgba(255,255,255,.15);
    white-space:nowrap;
    position:relative;
  }
  .vv-collapse{
    position:absolute;
    top:12px;
    right:12px;
    width:34px;
    height:34px;
    border-radius:999px;
    border:1px solid rgba(255,255,255,.25);
    background:#132a55;
    color:#fff;
    font-weight:800;
    cursor:pointer;
  }

  .vv-items{
    list-style:none;
    margin:8px 0 0;
    padding:0 2px 0 4px;
  }

  /* Botones de capítulos: texto alineado a la IZQUIERDA, estilo formal */
  .vv-itemBtn{
    all:unset;
    box-sizing:border-box;
    display:grid;
    grid-template-columns:40px 1fr auto;
    align-items:center;
    gap:10px;
    width:100%;
    padding:10px 12px;
    margin:2px 4px;
    cursor:pointer;
    border-radius:10px;
    color:#dbeafe;
    background:transparent;
    text-align:left;
  }
  .vv-itemBtn:hover{
    background:#102650;
  }
  .vv-itemActive{
    background:#132a55;
    outline:1px solid rgba(255,255,255,.10);
    color:#ffffff;
  }

  .vv-num {
    display:inline-grid;
    place-items:center;
    width:28px;
    height:28px;
    min-width:28px;
    border-radius:8px;
    background:#0b2a68;
    color:#dbeafe;
    font-weight:800;
    font-size:13px;
    letter-spacing:.05em;
    border:1px solid rgba(255,255,255,.15);
  }
  .vv-itemActive .vv-num{
    background:#174ea6;
    color:#fff;
    border-color: rgba(255,255,255,.28);
  }

  .vv-itemLabel{
    font-size:14px;
    line-height:1.3;
    display:-webkit-box;
    -webkit-line-clamp:2;
    -webkit-box-orient:vertical;
    overflow:hidden;
  }

  .vv-wrap[data-collapsed="true"] .vv-itemLabel{ display:none; }
  .vv-wrap[data-collapsed="true"] .vv-itemBtn{
    grid-template-columns:1fr;
    justify-items:center;
  }
  .vv-wrap[data-collapsed="true"] .vv-title{
    font-size:0;
    padding-right:52px;
  }
  .vv-wrap[data-collapsed="true"] .vv-title::after{
    content:"B";
    font-size:16px;
    font-weight:900;
    letter-spacing:.1em;
  }

  .vv-side-footer{
    border-top:1px solid ${SIDEBAR_BORDER};
    padding:10px 12px;
    color:#9fb7ff;
    font-size:12px;
    background:#0d1f48;
  }

  .vv-viewer{
    margin-left:var(--sb-w);
    padding:18px clamp(12px, 2vw, 28px);
    min-height:calc(100dvh - var(--header-offset));
    display:flex;
    justify-content:center;
    align-items:flex-start;
    background:#ffffff;
    overflow-x:hidden;
  }
  .vv-frameWrap{
    width:98%;
    max-width:1200px;
  }

  /* ====== BARRA FIJA DE PROGRESO ====== */
  /* Contenedor fijo que se alinea con el header del sidebar */
  .vv-courseBar-fixed{
    position:fixed;
      top: calc(var(--header-offset) - 8px);
    left:var(--sb-w);
    right:0;
    z-index:40;
    padding:10px 18px;
    background:#ffffff;
  }

  /* Caja blanca de la barra */
  .vv-courseBar{
    margin:0 auto;
    width:100%;
    background:#f9fafb;
    border:1px solid #e5e7eb;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:16px;
    padding:10px 16px;
  }

  /* Espaciador para que el contenido no quede oculto bajo la barra fija */
  .vv-courseBar-spacer{
    height:70px;
  }

  .vv-courseBar-left{
    display:flex;
    flex-direction:column;
    gap:4px;
  }
  .vv-courseBar-label{
    font-size:13px;
    color:#0f172a;
  }
  .vv-courseBar-label strong{
    font-weight:700;
  }
  .vv-courseBar-steps{
    margin-left:8px;
    color:#4b5563;
  }
  .vv-courseBar-track{
    width:230px;
    max-width:52vw;
    height:6px;
    border-radius:999px;
    background:#e5e7eb;
    overflow:hidden;
  }
  .vv-courseBar-fill{
    height:100%;
    border-radius:inherit;
    background:#1d4ed8;
    transition:width .25s ease-out;
  }
  .vv-courseBar-nav{
    display:flex;
    align-items:center;
    gap:18px;
    font-size:14px;
  }
  .vv-courseBar-btn{
    all:unset;
    cursor:pointer;
    display:inline-flex;
    align-items:center;
    gap:6px;
    color:#0b63ce;
    font-weight:600;
  }
  .vv-courseBar-btn span:first-child{
    font-size:16px;
  }
  .vv-courseBar-btn[disabled]{
    opacity:.35;
    cursor:default;
    pointer-events:none;
  }
  .vv-courseBar-divider{
    width:1px;
    height:22px;
    background:#e5e7eb;
  }

  @media (max-width:768px){
    .vv-courseBar{
      flex-direction:column;
      align-items:flex-start;
    }
    .vv-courseBar-nav{
      width:100%;
      justify-content:space-between;
    }
    .vv-courseBar-track{
      width:100%;
      max-width:none;
    }
    /* en móvil la barra ocupa todo el ancho */
    .vv-courseBar-fixed{
      left:0;
    }
  }

  .vv-article h2{
    font-size:clamp(34px, 4vw, 52px);
    margin:0 0 12px;
    color:#111827;
    line-height:1.15;
    letter-spacing:.2px;
    text-align:center;
  }
  .vv-article h3{
    font-size:clamp(24px, 2.6vw, 34px);
    margin:20px 0 10px;
    color:#111827;
    line-height:1.25;
  }
  .vv-article h4{
    font-size:clamp(20px, 2.1vw, 26px);
    margin:14px 0 8px;
    color:#111827;
    line-height:1.3;
  }

  /* BREADCRUMB debajo del título */
  .vv-breadcrumb{
    margin:0 0 14px;
    padding:8px 12px;
    border-radius:10px;
    background:#fff;
    display:flex;
    align-items:center;
    gap:4px;
    font-size:14px;
  }
  .vv-breadcrumb-link{
    all:unset;
    cursor:pointer;
    color:#0b63ce;
    font-weight:600;
  }
  .vv-breadcrumb-link:hover{
    text-decoration:underline;
  }
  .vv-breadcrumb-sep{
    color:#9ca3af;
    padding:0 2px;
  }
  .vv-breadcrumb-current{
    color:#111827;
  }

  .vv-article p,
  .vv-article li{
    font-size:clamp(17px, 1.6vw, 21px);
    line-height:1.85;
    color:#111827;
    text-align:left;
    hyphens:auto;
    overflow-wrap:anywhere;
  }

  .vv-card{
    padding:clamp(16px,2.4vw,26px);
    background:#ffffff;
    margin-bottom:18px;
 
  }

  .vv-toc .vv-toc-list,
  .vv-toc .vv-toc-sublist{
    margin: 6px 0 0 0;
    padding-left: 1.25rem;
  }
  .vv-toc .vv-toc-sublist{ margin-top: 4px; }
  .vv-toc .vv-toc-link{
    all:unset;
    cursor:pointer;
    color:#1d4ed8;
    border-bottom:1px dashed rgba(37,99,235,.35);
    font-size:clamp(15px, 1.4vw, 18px);
  }
  .vv-toc .vv-toc-link:hover{ color:#1e3a8a; }

  .vv-figure{
    margin:10px 0 16px;
    text-align:center;
  }
  .vv-figure img{
    max-width:60%;
    height:auto;
    border-radius:6px;
    border:1px solid #e5e7eb;
    box-shadow:none;
  }
  .vv-figure figcaption{
    margin-top:6px;
    font-size:clamp(13px, 1.1vw, 15px);
    color:#6b7280;
    text-align:center;
  }

  .vv-list{
    padding-left:1.25em;
    margin:8px 0;
  }

 .vv-table-wrap{
    margin-top:12px;
    border-radius:12px;
    overflow:auto;
    border:1px solid #e5e7eb;
    box-shadow:0 1px 2px rgba(15,23,42,.04);
    background:#ffffff;
  }

  .vv-table{
    width:100%;
    border-collapse:collapse;
    table-layout:fixed;
  }

   /* Opcional: título grande arriba tipo “TIPOS DE BLOCKCHAIN” si usas <caption> */
  .vv-table caption{
    caption-side:top;
    background:#1d4ed8;
    color:#ffffff;
    font-weight:800;
    text-transform:uppercase;
    letter-spacing:.03em;
    padding:10px 16px;
    text-align:left;
    font-size:14px;
  }

  /* Encabezado principal (fila de PÚBLICA / PRIVADA / etc.) */
  .vv-table thead th{
    background:#374151;
    color:#f9fafb;
    font-size:14px;
    font-weight:700;
    padding:12px 14px;
    border-bottom:1px solid #4b5563;
  }

  /* Primera columna del header (vacía o “CARACTERÍSTICAS”) más oscura */
  .vv-table thead th:first-child{
    background:#111827;
    text-align:left;
    width:32%;
  }

  /* Celdas genéricas */
  .vv-table th,
  .vv-table td{
    padding:10px 14px;
    border-bottom:1px solid #e5e7eb;
    font-size:15px;
  }

  /* Primer columna: texto de característica alineado a la izquierda */
  .vv-table tbody td:first-child{
    font-weight:600;
    color:#111827;
    text-align:left;
  }

  /* Columnas de tipo de blockchain centradas */
  .vv-table tbody td:not(:first-child),
  .vv-table thead th:not(:first-child){
    text-align:center;
  }

  /* Filas tipo “zebra” como en el ejemplo */
  .vv-table tbody tr:nth-child(odd){
    background:#f9fafb;
  }
  .vv-table tbody tr:nth-child(even){
    background:#ffffff;
  }

  /* Hover suave */
  .vv-table tbody tr:hover{
    background:#e5f0ff;
  }
    
  .vv-code{
    position:relative;
    background:#020617;
    color:#f9fafb;
    padding:14px 16px 12px;
    border-radius:10px;
    overflow:auto;
    border:1px solid #334155;
    margin-top:12px;
  }
  .vv-code.small{ font-size:13px; }

  .vv-code::before{
    content: attr(data-lang-label);
    position:absolute;
    top:6px;
    right:10px;
    font-size:10px;
    text-transform:uppercase;
    letter-spacing:.08em;
    color:#e5e7eb;
    background:rgba(15,23,42,0.9);
    border-radius:999px;
    padding:2px 7px;
    border:1px solid #4b5563;
  }

  .vv-code[data-lang="javascript"],
  .vv-code[data-lang="js"]{
    border-top:3px solid #facc15;
  }
  .vv-code[data-lang="solidity"]{
    border-top:3px solid #22d3ee;
  }
  .vv-code[data-lang="java"]{
    border-top:3px solid #f97316;
  }
  .vv-code[data-lang="python"]{
    border-top:3px solid #22c55e;
  }
  .vv-code[data-lang="rust"]{
    border-top:3px solid #a855f7;
  }
  .vv-code[data-lang="haskell"]{
    border-top:3px solid #6366f1;
  }
  .vv-code[data-lang="p5js"],
  .vv-code[data-lang="p5"]{
    border-top:3px solid #ec4899;
  }

  .vv-subsection{
    margin-top:10px;
    padding-top:6px;
    border-top:1px dashed #e5e7eb;
  }

  .vv-inline-code{
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
      "Liberation Mono", monospace;
    font-size:.9em;
    background:#020617;
    color:#f9fafb;
    padding:.08em .35em;
    border-radius:5px;
    border:1px solid #334155;
    white-space:nowrap;
  }

  .vv-gloss{
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: help;
    border-radius:4px;
    padding:0 .08em;
  }
  .vv-gloss:hover{
    background:#fef9c3;
  }

  .vv-linkFrame{
    all:unset;
    cursor:pointer;
    display:inline-flex;
    align-items:center;
    gap:4px;
    padding:0 2px;
    color:#1d4ed8;
    border-bottom:1px solid rgba(37,99,235,.35);
    border-radius:4px;
  }
  .vv-linkFrame:hover{
    background:rgba(37,99,235,0.06);
    color:#1e3a8a;
  }
  .vv-linkFrame-icon{
    font-size:.8em;
    opacity:.75;
  }

  .vv-notes{
    margin-top:14px;
    padding:10px 12px;
    border-radius:10px;
    background:#f9fafb;
    border:1px dashed #e5e7eb;
  }
  .vv-notes-title{
    font-size:13px;
    font-weight:700;
    color:#111827;
    text-transform:uppercase;
    letter-spacing:.04em;
    margin-bottom:6px;
  }
  .vv-notes ul{
    margin:0;
    padding-left:1.1em;
  }
  .vv-notes li{
    font-size:clamp(14px, 1.3vw, 17px);
    line-height:1.6;
    color:#111827;
  }

  .vv-bluepoints{
    margin:12px 0;
    display:flex;
    flex-direction:column;
    gap:8px;
    text-align:left;
  }
  .vv-bluepoint{
    display:flex;
    gap:10px;
    padding:10px 12px;
    border-radius:10px;
    background:#f9fafb;
    border:1px solid #e5e7eb;
  }
  .vv-bluepoint-badge{
    width:6px;
    border-radius:999px;
    background:#1d4ed8;
    flex-shrink:0;
  }
  .vv-bluepoint-body{
    flex:1;
  }
  .vv-bluepoint-title{
    margin:0 0 0px;
    font-size:clamp(15px,1.4vw,18px);
    color:#111827;
    font-weight:700;
  }

  .vv-externalFrame{
    margin-top:18px;
    border-radius:12px;
    background:#ffffff;
    overflow:hidden;
  }
  .vv-externalFrame-header{
    padding:10px 14px;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:8px;
    background:#f9fafb;
  }
  .vv-externalFrame-title{
    font-size:14px;
    font-weight:600;
    color:#111827;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
  }
  .vv-externalFrame-url{
    font-size:12px;
    color:#6b7280;
    opacity:.9;
  }
  .vv-externalFrame-close{
    all:unset;
    cursor:pointer;
    width:26px;
    height:26px;
    border-radius:999px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    background:#f9fafb;
    font-size:16px;
  }
  .vv-externalFrame-close:hover{
    background:#e5e7eb;
  }
  .vv-externalFrame iframe{
    width:100%;
    height:420px;
    border:0;
    background:#ffffff;
  }

  @media (max-width: 768px){
    .vv-externalFrame iframe{
      height:360px;
    }
  }
`}</style>

      {/* SIDEBAR */}
      <aside
        className="vv-sidebar"
        aria-label="Navegación de Contenidos"
      >
        <h1 className="vv-title">
          <span>Blockchain Esencial</span>
          <button
            className="vv-collapse"
            title={collapsed ? "Expandir" : "Colapsar"}
            onClick={() => setCollapsed((v) => !v)}
            aria-label="Alternar sidebar"
          >
            {collapsed ? "›" : "‹"}
          </button>
        </h1>

        <div className="vv-side-scroll">
          <ul className="vv-items">
            {contentsData.map((item) => {
              const active = selected.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    className={`vv-itemBtn ${
                      active ? "vv-itemActive" : ""
                    }`}
                    onClick={() => handleSelect(item)}
                    aria-current={active ? "page" : undefined}
                    title={item.title}
                  >
                    <span className="vv-num">{pad2(item.id)}</span>
                    <span className="vv-itemLabel">{item.title}</span>
                    {active ? (
                      <span
                        style={{
                          color: "#8ec5ff",
                          fontWeight: 800,
                        }}
                      >
                        •
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="vv-side-footer">
          <div>v1.0 • UVEG • CoachBlockchain</div>
        </div>
      </aside>

      {/* VIEWER */}
      <main className="vv-viewer">
        <div className="vv-frameWrap">
          {/* CONTENEDOR FIJO DE LA BARRA */}
          <div className="vv-courseBar-fixed" aria-label="Progreso del curso">
            <section className="vv-courseBar">
              <div className="vv-courseBar-left">
                <div className="vv-courseBar-label">
                  <strong>{progressPercent}% COMPLETO</strong>
                  <span className="vv-courseBar-steps">
                    {safeIndex + 1}/{totalLessons} Lecciones
                  </span>
                </div>
                <div className="vv-courseBar-track">
                  <div
                    className="vv-courseBar-fill"
                    style={{ width: `${progressRatio * 100}%` }}
                  />
                </div>
              </div>

              <div className="vv-courseBar-nav">
                <button
                  type="button"
                  className="vv-courseBar-btn"
                  onClick={goPrev}
                  disabled={!prevItem}
                >
                  <span>‹</span>
                  <span>Anterior lección</span>
                </button>

                <div className="vv-courseBar-divider" />

                <button
                  type="button"
                  className="vv-courseBar-btn"
                  onClick={goNext}
                  disabled={!nextItem}
                >
                  <span>Siguiente lección</span>
                  <span>›</span>
                </button>
              </div>
            </section>
          </div>

          {/* ESPACIADOR para que el contenido no se esconda debajo */}
          <div className="vv-courseBar-spacer" />

          {ActiveComponent ? (
            <ActiveComponent
              onOpenFrame={handleOpenFrame}
              courseName="Blockchain Esencial"
              onGoToCourseRoot={goRoot}
            />
          ) : (
            <div
              style={{
                padding: 24,
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0, color: "#111827" }}>
                Configura este ítem en <code>DARK_FOREST_CONTENTS</code>.
              </p>
            </div>
          )}

          {frame.open && (
            <section
              className="vv-externalFrame"
              aria-label="Vista previa de sitio externo"
            >
              <header className="vv-externalFrame-header">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                  }}
                >
                  <span className="vv-externalFrame-title">
                    {frame.label || "Vista previa"}
                  </span>
                  <span className="vv-externalFrame-url">
                    {frame.url}
                  </span>
                </div>
                <button
                  type="button"
                  className="vv-externalFrame-close"
                  onClick={handleCloseFrame}
                  aria-label="Cerrar vista previa"
                >
                  ×
                </button>
              </header>
              <iframe
                src={frame.url}
                title={frame.label || frame.url}
                loading="lazy"
              />
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
